import { useEffect, useState, type FormEvent } from "react";
import api from "@/api/axios";

type MiscConfig = {
  use_time_range?: boolean;
  interval_mode?: "random" | "fixed";
  start_time?: string;
  end_time?: string;
  interval_min?: number;
  interval_max?: number;
  fixed_interval?: number;
};

export default function MiscSection({ config, refresh, preview = false }: { config: MiscConfig; refresh: () => void; preview?: boolean }) {
  const [useTimeRange, setUseTimeRange] = useState(config.use_time_range ?? true);
  const [intervalMode, setIntervalMode] = useState<"random" | "fixed">(config.interval_mode ?? "random");
  const [startTime, setStartTime] = useState(config.start_time ?? "00:00");
  const [endTime, setEndTime] = useState(config.end_time ?? "23:59");
  const [intervalMin, setIntervalMin] = useState(config.interval_min ?? 25);
  const [intervalMax, setIntervalMax] = useState(config.interval_max ?? 35);
  const [fixedInterval, setFixedInterval] = useState(config.fixed_interval ?? 30);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setUseTimeRange(config.use_time_range ?? true);
    setIntervalMode(config.interval_mode ?? "random");
    setStartTime(config.start_time ?? "00:00");
    setEndTime(config.end_time ?? "23:59");
    setIntervalMin(config.interval_min ?? 25);
    setIntervalMax(config.interval_max ?? 35);
    setFixedInterval(config.fixed_interval ?? 30);
  }, [config]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess(false);
    if (useTimeRange && startTime >= endTime) {
      setError("结束时间需要晚于开始时间。");
      return;
    }
    if (intervalMode === "random" && intervalMin > intervalMax) {
      setError("最大间隔不能小于最小间隔。");
      return;
    }
    if (preview) {
      setError("这是界面预览，执行时间不会保存。");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/save_misc_config", {
        use_time_range: useTimeRange,
        interval_mode: intervalMode,
        start_time: useTimeRange ? startTime : "00:00",
        end_time: useTimeRange ? endTime : "23:59",
        interval_min: intervalMode === "random" ? intervalMin : undefined,
        interval_max: intervalMode === "random" ? intervalMax : undefined,
        fixed_interval: intervalMode === "fixed" ? fixedInterval : undefined,
      });
      setSuccess(true);
      refresh();
    } catch {
      setError("保存失败，请稍后重试。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="user-config-section user-schedule" onSubmit={save}>
      <div className="user-config-toolbar"><div><h3>执行时间</h3><p>设置打卡时段与两次打卡之间的间隔</p></div></div>
      <label className="user-schedule-toggle"><span><strong>限定打卡时段</strong><small>关闭后全天可以执行打卡</small></span><input type="checkbox" checked={useTimeRange} onChange={(event) => { setUseTimeRange(event.target.checked); setSuccess(false); }} /></label>
      {useTimeRange && <div className="user-schedule-fields"><label>开始时间<input type="time" required value={startTime} onChange={(event) => { setStartTime(event.target.value); setSuccess(false); }} /></label><label>结束时间<input type="time" required value={endTime} onChange={(event) => { setEndTime(event.target.value); setSuccess(false); }} /></label></div>}
      <div className="user-schedule-divider" />
      <fieldset className="user-schedule-mode"><legend>打卡间隔</legend><div className="user-schedule-choices"><label><input type="radio" name="intervalMode" checked={intervalMode === "random"} onChange={() => { setIntervalMode("random"); setSuccess(false); }} />随机间隔</label><label><input type="radio" name="intervalMode" checked={intervalMode === "fixed"} onChange={() => { setIntervalMode("fixed"); setSuccess(false); }} />固定间隔</label></div></fieldset>
      {intervalMode === "random" ? <div className="user-schedule-fields"><label>最小间隔（分钟）<input type="number" min="1" required value={intervalMin} onChange={(event) => { setIntervalMin(Number(event.target.value)); setSuccess(false); }} /></label><label>最大间隔（分钟）<input type="number" min="1" required value={intervalMax} onChange={(event) => { setIntervalMax(Number(event.target.value)); setSuccess(false); }} /></label></div> : <div className="user-schedule-fields"><label>固定间隔（分钟）<input type="number" min="1" required value={fixedInterval} onChange={(event) => { setFixedInterval(Number(event.target.value)); setSuccess(false); }} /></label></div>}
      {error && <p className="user-form-error" role="alert">{error}</p>}
      {success && <p className="user-form-success" role="status">设置已保存</p>}
      <div className="user-config-footer"><span>修改后需保存才能生效</span><button type="submit" className="user-button user-button-primary" disabled={saving}>{saving ? "保存中…" : "保存设置"}</button></div>
    </form>
  );
}
